import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';

import {
  LayoutDashboard,
  ArrowRightLeft,
  ArrowDownToLine,
  Search,
  GitBranch,
  Menu,
  X,
  Users as UsersIcon,
  Shield,
  ShieldCheck,
  LogOut,
  ScrollText,
  Gauge,
  BarChart2,
  FileText,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Key,
  Lock,
  Rocket,
  Server,
  Webhook,
  AlertOctagon,
  BookOpen,
  FileCode2,
  CheckSquare,
  ListChecks,
  Play,
  Workflow,
  CreditCard,
  RefreshCw,
  UserPlus,
  Waypoints,
  Library,
  Hash, Wand2,

  ScanSearch, BookMarked,
  Globe2, BookText, Inbox, Coins, FlaskConical,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSchemes } from '../contexts/SchemeContext';
import ProfileStatusBadge from './xb/ProfileStatusBadge';
import { shortSchemeName } from '../utils/crossBorder';
import ErrorBoundary from './ErrorBoundary';
import {
  CORRIDORS, assignedCorridors, corridorForPath, corridorForSharedPage, isSharedPath, rememberCorridor,
} from '../utils/corridors';
import { perfTestApi, systemApi } from '../services/api';

// ---------------------------------------------------------------------------
// Logo
// ---------------------------------------------------------------------------
const SWITCH_LOGO_URL =
  import.meta.env.VITE_SWITCH_LOGO?.trim() || '/switch-logo.png';

// ---------------------------------------------------------------------------
// FSPIOP corridor navigation
// ---------------------------------------------------------------------------
const FSPIOP_NAV = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },

  { section: 'Build & test' },
  { name: 'Getting Started',     href: '/getting-started',        icon: Rocket   },
  { name: 'Flow Simulator',      href: '/guides/flow-simulator',  icon: Workflow },
  { name: 'Signing Guide (JWS)', href: '/guides/signing',         icon: Key      },
  { name: 'Async Callbacks',     href: '/guides/async-callbacks', icon: RefreshCw },
  { name: 'ILP Fulfilment',      href: '/guides/ilp',             icon: Lock     },
  { name: 'CBS Payee Webhook',   href: '/guides/cbs-webhook',     icon: Webhook  },
  { name: 'Error Codes',         href: '/error-codes',            icon: AlertOctagon },

  { section: 'Operations' },
  {
    name: 'FSPIOP Transactions',
    icon: FolderOpen,
    submenu: [
      { name: 'Transfers',      href: '/transfers',      icon: ArrowRightLeft  },
      { name: 'Party Lookups',  href: '/party-lookups',  icon: Search          },
      { name: 'Quotes',         href: '/quotes',         icon: GitBranch       },
      { name: 'Payee Requests', href: '/payee-requests', icon: ArrowDownToLine },
    ],
  },

  { section: 'Security' },
  {
    name: 'Certificates',
    icon: Shield,
    submenu: [
      { name: 'Signing Certificates', href: '/certificates', icon: Shield      },
      { name: 'FSP Keys',             href: '/fsp-keys',     icon: ShieldCheck },
    ],
  },

  { section: 'Certify' },
  { name: 'Certification', href: '/certification', icon: ShieldCheck },

  { section: 'Administration', adminOnly: true },
  { name: 'Users',            href: '/users',            icon: UsersIcon, adminOnly: true },
  // Switch Endpoints was removed from this corridor: the rows it edited were
  // ISO 20022 message types that the FSPIOP engine never routed on. It returns
  // with the Hub ISO 20022 connector.
  {
    name: 'Reporting',
    icon: BarChart2,
    adminOnly: true,
    submenu: [
      { name: 'Application Logs', href: '/logs',         icon: ScrollText },
      { name: 'Audit Export',     href: '/audit-export', icon: FileText   },
    ],
  },
  { name: 'Performance Test', href: '/perf',     icon: Gauge,    adminOnly: true, perfOnly: true },
];

// ---------------------------------------------------------------------------
// ISO 20022 corridor navigation
// ---------------------------------------------------------------------------
const ISO20022_NAV = [
  { name: 'Dashboard', href: '/iso20022/dashboard', icon: LayoutDashboard },

  // Journey order: understand the flow → build → check.
  { section: 'Build & test' },
  { name: 'Getting Started',   href: '/iso20022/getting-started', icon: Rocket    },
  { name: 'Flow Simulator',    href: '/iso20022/simulator',       icon: Workflow  },
  { name: 'Signing Guide',     href: '/iso20022/signing-guide',   icon: Key       },
  { name: 'Message Generator', href: '/iso20022/generate',      icon: FileCode2   },
  { name: 'Message Validator', href: '/iso20022/validate',      icon: CheckSquare },
  { name: 'Error Codes',       href: '/iso20022/error-codes',   icon: AlertOctagon },

  { section: 'Connectivity' },
  { name: 'Signing Certificates', href: '/iso20022/certificates',        icon: Shield  },
  { name: 'Callback Endpoints',   href: '/iso20022/callback-endpoints',  icon: Webhook },

  { section: 'Certify' },
  {
    name: 'Certification',
    icon: ShieldCheck,
    submenu: [
      { name: 'Run & report',       href: '/iso20022/certification',       icon: Play },
      { name: 'Certification scope', href: '/iso20022/certification/scope', icon: ListChecks },
    ],
  },
  { name: 'Self-Test',         href: '/iso20022/conformance',   icon: CheckSquare },

  { section: 'Go live' },
  { name: 'Switch Deployment', href: '/iso20022/deployment',    icon: Server },

  { section: 'Operations' },
  {
    name: 'Message Exchange',
    icon: FolderOpen,
    submenu: [
      { name: 'Transfers',       href: '/iso20022/operations/transfers',     icon: ArrowRightLeft  },
      { name: 'Account Lookups', href: '/iso20022/operations/lookups',       icon: Search          },
      { name: 'Registrations',   href: '/iso20022/operations/registrations', icon: ArrowDownToLine },
      { name: 'Async Traces',    href: '/iso20022/operations/traces',        icon: Waypoints       },
      { name: 'All Messages',    href: '/iso20022/ledger',                   icon: BookOpen        },
    ],
  },

  { section: 'Administration', adminOnly: true },
  { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
];

// ---------------------------------------------------------------------------
// ISO 8583 card corridor navigation
// ---------------------------------------------------------------------------
const ISO8583_NAV = [
  { name: 'Dashboard', href: '/iso8583/dashboard', icon: LayoutDashboard },

  { section: 'Build & test' },
  { name: 'Getting Started',   href: '/iso8583/getting-started',  icon: Rocket       },
  { name: 'Test Cards',        href: '/iso8583/test-cards',       icon: CreditCard   },
  { name: 'Codes & Messages',  href: '/iso8583/codes',            icon: Hash         },
  { name: 'Client Libraries',  href: '/iso8583/client-libraries', icon: Library      },
  // The three pages that work on real bytes: build a message, read one back, and
  // see the dialect both are measured against. Their own section rather than a
  // collapsible group — the pages above are read once, these are returned to all
  // day, and a click to reach them every time is a click too many.
  { section: 'Message tools' },
  { name: 'Message Generator', href: '/iso8583/message-generator', icon: Wand2       },
  { name: 'Explain a Frame',   href: '/iso8583/explain',           icon: ScanSearch  },
  { name: 'Dialect',           href: '/iso8583/dialect',           icon: BookMarked  },

  { section: 'Certify' },
  { name: 'Certification',     href: '/iso8583/certification',    icon: ShieldCheck  },

  { section: 'Operations' },
  { name: 'Message Ledger',    href: '/iso8583/ledger',           icon: BookOpen     },

  { section: 'Administration', adminOnly: true },
  { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
];

// ---------------------------------------------------------------------------
// Cross-border corridor navigation: one scheme at a time, chosen above the menu.
// An item with `offered` shows only where the chosen scheme's profile offers it.
// ---------------------------------------------------------------------------
const XB_NAV = [
  { name: 'Overview', href: '/xb/overview', icon: Globe2 },

  { section: 'Build & test' },
  { name: 'Getting Started',  href: '/xb/getting-started', icon: Rocket },
  { name: 'Scheme Profile',   href: '/xb/profile',         icon: BookText },
  { name: 'Message Samples',  href: '/xb/samples',         icon: FlaskConical },
  { name: 'FX Quotes',        href: '/xb/quotes',          icon: Coins, offered: (s) => s?.offers?.quotes },

  { section: 'Operations' },
  { name: 'Incoming Payments', href: '/xb/incoming',       icon: Inbox },

  { section: 'Administration', adminOnly: true },
  { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
];

const NAV_BY_CORRIDOR = { fspiop: FSPIOP_NAV, iso20022: ISO20022_NAV, iso8583: ISO8583_NAV, xb: XB_NAV };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function rootSegment(href) {
  const parts = href.split('/').filter(Boolean);
  return parts.length ? `/${parts[0]}` : href;
}

function isSubmenuActive(submenu, pathname) {
  return submenu.some((item) =>
    pathname === item.href || pathname.startsWith(item.href + '/')
  );
}

function useCorridorFromPath(pathname, user, schemes) {
  return (isSharedPath(pathname) ? corridorForSharedPage(user, schemes) : corridorForPath(pathname)).id;
}

// ---------------------------------------------------------------------------
// UserAvatar
// ---------------------------------------------------------------------------
function UserAvatar({ fullName, className = '' }) {
  const initials = fullName
    ? fullName.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
    : 'U';
  return <span className={className}>{initials}</span>;
}

// ---------------------------------------------------------------------------
// CorridorSwitcher
// ---------------------------------------------------------------------------
function CorridorSwitcher({ corridor }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { schemes } = useSchemes();

  const assigned = assignedCorridors(user, schemes);
  const tabs = CORRIDORS
    .filter((c) => assigned.includes(c.code))
    .map((c) => ({ id: c.id, code: c.code, label: c.label, target: c.home }));

  // Single-corridor participant → no switcher (they only ever see their own corridor).
  if (tabs.length <= 1) return null;

  return (
    <div className="px-4 pb-3 border-b border-gray-200">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2 font-semibold">Corridor</p>
      {/* Four tabs do not fit one row of the sidebar: they go two by two. */}
      <div className={`${tabs.length > 3 ? 'grid grid-cols-2 gap-px bg-gray-200' : 'flex'} rounded-lg border border-gray-200 overflow-hidden text-xs font-medium`}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.target)}
            aria-pressed={corridor === tab.id}
            className={`flex-1 py-1.5 transition-colors ${
              corridor === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SchemePicker: which scheme the Cross-border pages show
// ---------------------------------------------------------------------------
function SchemePicker() {
  const { schemes, selected, select } = useSchemes();
  if (!schemes?.length || !selected) return null;
  const name = (s) => shortSchemeName(s.profile) || s.profileId;
  return (
    <div className="px-4 pb-3 border-b border-gray-200">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2 font-semibold">Scheme</p>
      {schemes.length > 1 ? (
        <select value={selected.profileId} onChange={(e) => select(e.target.value)} aria-label="Scheme"
          className="w-full px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900">
          {schemes.map((s) => <option key={s.profileId} value={s.profileId}>{name(s)}</option>)}
        </select>
      ) : (
        <p className="text-sm font-medium text-gray-900 truncate" title={selected.profile?.name}>{name(selected)}</p>
      )}
      <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500 min-w-0">
        {selected.profile && <ProfileStatusBadge status={selected.profile.status} />}
        <span className="font-mono truncate">{selected.profile?.ref || selected.profileId}</span>
      </div>
      {selected.problem && (
        <p className="mt-1.5 text-xs text-red-600">Cannot be used as assigned: {selected.problem}.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavGroupItem
// ---------------------------------------------------------------------------
function NavGroupItem({ item, pathname }) {
  const Icon = item.icon;
  const isActive = isSubmenuActive(item.submenu, pathname);
  const [open, setOpen] = useState(isActive);

  // Re-open when corridor changes and a submenu item becomes active
  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  return (
    <div>
      <button
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
          isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <Icon className="w-5 h-5 mr-3 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">{item.name}</span>
        {open
          ? <ChevronDown className="w-4 h-4 ml-auto shrink-0" aria-hidden="true" />
          : <ChevronRight className="w-4 h-4 ml-auto shrink-0" aria-hidden="true" />}
      </button>

      {open && (
        <div className="ml-4 mt-1 space-y-1" role="group" aria-label={item.name}>
          {item.submenu.map((subItem) => {
            const SubIcon = subItem.icon;
            const active = pathname === subItem.href
              || pathname.startsWith(subItem.href + '/');
            return (
              <Link
                key={subItem.href}
                to={subItem.href}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  active ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <SubIcon className="w-4 h-4 mr-3 shrink-0" aria-hidden="true" />
                {subItem.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [perfEnabled, setPerfEnabled] = useState(false);
  const [dfspName, setDfspName]       = useState('');
  const location  = useLocation();
  const { user, logout } = useAuth();
  const { schemes, selected: scheme } = useSchemes();

  const corridor = useCorridorFromPath(location.pathname, user, schemes);
  const navigate = useNavigate();

  // A corridor's own page sets the corridor a shared page (Users, Change Password)
  // then keeps, so opening one does not swap the sidebar for FSPIOP's.
  useEffect(() => {
    if (!isSharedPath(location.pathname)) rememberCorridor(corridor);
  }, [location.pathname, corridor]);

  // Portal-side corridor gating — keep a participant within an assigned corridor.
  useEffect(() => {
    if (!user) return;
    const here = corridorForPath(location.pathname);
    // Cross-border is held through scheme profiles: wait until they are known.
    if (here.code === 'XB' && schemes === null) return;
    const assigned = assignedCorridors(user, schemes);
    // A corridor page the participant is not assigned, or the FSPIOP dashboard
    // for one who is not on FSPIOP, sends them to their first assigned corridor.
    const outside = here.prefix ? !assigned.includes(here.code)
                                : location.pathname === '/dashboard' && !assigned.includes('FSPIOP');
    if (outside) {
      const home = CORRIDORS.find((c) => assigned.includes(c.code));
      if (home && home.home !== location.pathname) navigate(home.home, { replace: true });
    }
  }, [location.pathname, user, navigate, schemes]);

  useEffect(() => {
    perfTestApi.getConfig()
      .then(cfg => setPerfEnabled(cfg?.enabled === true))
      .catch(() => setPerfEnabled(false));

    systemApi.getIdentity()
      .then(d => setDfspName(d.displayName || d.fspId || ''))
      .catch(() => {});
  }, []);

  const activeNav = NAV_BY_CORRIDOR[corridor] || FSPIOP_NAV;

  const visibleNav = activeNav.filter((item) => {
    if (item.adminOnly && user?.role !== 'ADMIN') return false;
    if (item.perfOnly && !perfEnabled) return false;
    if (item.offered && !item.offered(scheme?.profile)) return false;
    return true;
  });

  // Drop section headers that have no visible items before the next section.
  const filteredNav = visibleNav.filter((item, i) => {
    if (!item.section) return true;
    for (let j = i + 1; j < visibleNav.length; j++) {
      if (visibleNav[j].section) break;   // hit next header → this one is empty
      return true;                        // found a real item under this header
    }
    return false;
  });

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) logout();
  };

  const corridorLabel  = { iso20022: 'ISO 20022', iso8583: 'ISO 8583', xb: 'Cross-border' }[corridor] || 'FSPIOP v1.1';
  const corridorColour = { iso20022: 'text-amber-700', iso8583: 'text-emerald-700', xb: 'text-indigo-700' }[corridor]
    || 'text-blue-700';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Primary navigation"
      >
        {/* Logo + DFSP name */}
        <div className="flex flex-col border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center justify-center flex-1">
              <img
                src={SWITCH_LOGO_URL}
                alt="Switch logo"
                className="h-10 w-auto object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
              aria-label="Close sidebar"
            >
              <X className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>
          <div className="px-4 pb-3 text-center">
            <p className="text-xs font-semibold text-primary-700 truncate tracking-wide uppercase">
              {dfspName || 'IIPS Participant Portal'}
            </p>
          </div>
        </div>

        {/* User info */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
              <UserAvatar fullName={user?.fullName} className="text-primary-700 font-semibold text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.fullName ?? 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.role ?? 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Corridor switcher */}
        <div className="px-4 pt-3 shrink-0">
          <CorridorSwitcher corridor={corridor} />
        </div>
        {corridor === 'xb' && (
          <div className="px-4 pt-3 shrink-0">
            <SchemePicker />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto" aria-label="Sidebar">
          {filteredNav.map((item) => {
            if (item.section) {
              return (
                <p key={`sec-${item.section}`}
                   className="px-4 pt-4 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {item.section}
                </p>
              );
            }
            if (item.submenu) {
              return <NavGroupItem key={item.name} item={item} pathname={location.pathname} />;
            }
            const Icon = item.icon;
            const active = location.pathname === item.href
              || (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 mr-3 shrink-0" aria-hidden="true" />
                {item.name}
              </Link>
            );
          })}

          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors text-red-600 hover:bg-red-50 mt-2"
          >
            <LogOut className="w-5 h-5 mr-3 shrink-0" aria-hidden="true" />
            Logout
          </button>
        </nav>

        {/* Footer — shows active corridor */}
        <div className="p-4 border-t border-gray-200 shrink-0">
          <div className="flex items-center justify-between">
            <div className={`text-xs font-mono font-semibold whitespace-nowrap ${corridorColour}`}>{corridorLabel}</div>
            <div className="text-xs text-gray-400">AfricaNenda IIPS Sandbox</div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-200 ${sidebarOpen ? 'lg:pl-64' : ''}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen((prev) => !prev)}
              className="text-gray-500 hover:text-gray-700"
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              aria-expanded={sidebarOpen}
            >
              <Menu className="w-6 h-6" aria-hidden="true" />
            </button>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" role="status" aria-label="System online" />
                <span className="text-sm text-gray-600">System Online</span>
              </div>

              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <Link
                  to={`/users/${user?.id}`}
                  className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center hover:bg-primary-200 transition-colors"
                  aria-label="View profile"
                >
                  <UserAvatar fullName={user?.fullName} className="text-primary-700 font-semibold text-sm" />
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900 bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default Layout;
